import { Button, Group, Stack, Text, UnstyledButton } from "@mantine/core";
import type { ParseKeys } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  getLanguagePreference,
  setLanguagePreference,
  type LanguagePreference,
  type SupportedLocale,
} from "../i18n";
import { CountryFlag } from "./CountryFlag";
import { NavBarContainer } from "./NavBarContainer";

const languageOptionOrder: SupportedLocale[] = [
  "en-US",
  "es-MX",
  "fr-FR",
  "de-DE",
];

const languageLabels: Record<SupportedLocale, ParseKeys> = {
  "en-US": "language.english",
  "es-MX": "language.spanish",
  "de-DE": "language.german",
  "fr-FR": "language.french",
};

export function LanguagePage() {
  const { t } = useTranslation();
  const [languagePreference, setLanguagePreferenceState] =
    React.useState<LanguagePreference>(() => getLanguagePreference());

  const handleLanguageChange = async (value: LanguagePreference) => {
    setLanguagePreferenceState(value);
    await setLanguagePreference(value);
  };

  return (
    <NavBarContainer title={t("language.title")}>
      <Stack gap="md" maw={480}>
        <Text size="sm" c="dimmed">
          {t("language.description")}
        </Text>
        <Group gap="sm">
          {languageOptionOrder.map((locale) => {
            const isActive = languagePreference === locale;
            return (
              <UnstyledButton
                key={locale}
                onClick={() => void handleLanguageChange(locale)}
                aria-pressed={isActive}
                aria-label={t(languageLabels[locale])}
                p="sm"
                style={{
                  borderRadius: 8,
                  border: isActive
                    ? "2px solid var(--mantine-color-brand-5)"
                    : "1px solid var(--mantine-color-gray-3)",
                  background: isActive
                    ? "var(--mantine-color-brand-0)"
                    : "var(--mantine-color-white)",
                  minWidth: 96,
                }}
              >
                <Stack gap={6} align="center">
                  <CountryFlag locale={locale} size={28} />
                  <Text size="sm" fw={isActive ? 600 : 400}>
                    {t(languageLabels[locale])}
                  </Text>
                </Stack>
              </UnstyledButton>
            );
          })}
        </Group>
        <Button
          variant={languagePreference === "browser" ? "filled" : "light"}
          onClick={() => void handleLanguageChange("browser")}
        >
          {t("language.browser")}
        </Button>
      </Stack>
    </NavBarContainer>
  );
}
